import React, { ReactElement, useCallback, useMemo } from 'react';
import { Pressable, StyleProp, StyleSheet, ViewStyle } from 'react-native';
import { DocumentStatus, FileType } from '@alucio/aws-beacon-amplify/src/models';
import {
  DNABox,
  DNAButton,
  DNACheckbox,
  DNAText,
  GridList,
  Iffy,
  util,
  luxColors,
} from '@alucio/lux-ui';
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors';
import {
  ActionCallbacks,
  DNAChip,
  DNACustomFieldChipList,
  DNADocumentStatusChip,
  DNADocumentThumbnail,
  DNADocumentContextMenu,
  DNADocumentStatusDot,
  DNAPopover,
  useDNADocumentActions,
  ListFieldConfig,
} from 'src/components/DNA';
import { ScheduleDocumentTooltip } from 'src/components/DNA/Icon/ScheduleDocumentTooltip';
import { Variant, DocumentORM } from 'src/types/types';
import { useAppSettings } from 'src/state/context/AppSettings';
import { GRID_LIST_STYLES_VARIANTS_CONFIGS } from 'src/screens/MyFolders/constants';
import { VALUE_MAX_TEXT_LINES } from '../constants';
import { getFormattedModelValue } from '../util';
import DNADocumentSummaryPreview from 'src/components/DNA/Document/DNADocumentSummaryPreview';
import { supportsDocumentSummary } from 'src/components/DNA/Document/DNADocumentSummary.utils';

const S = StyleSheet.create({
  rowContainer: {
    paddingHorizontal: 8,
  },
  unavailableContentRowContainer: {
    opacity: 0.4,
  },
  unavailableContentBadge: {
    marginTop: 4,
  },
  fieldCellContainer: {
    paddingLeft: 12,
  },
  documentRowTitleText: {
    color: colors['color-text-light-primary'],
  },
  documentRowFieldText: {
    color: colors['color-text-light-tertiary'],
  },
  flexDirectionRow: {
    flexDirection: 'row',
  },
  checkbox: {
    margin: 6,
  },
});

interface DNADocumentListRowProps {
  documentORM: DocumentORM,
  variant: Variant,
  isDesktop: boolean,
  isOnline: boolean,
  onPress?: (doc: DocumentORM) => void,
  onThumbnailPress?: (doc: DocumentORM) => void,
  actionCallbacks?: ActionCallbacks,
  fields: ListFieldConfig[],
  cellStyle?: StyleProp<ViewStyle>
  rowStyle?: StyleProp<ViewStyle>
  selectEnabled?: boolean,
  checked?: boolean,
  onCheck?: (doc: DocumentORM) => void,
  renderBadgesInline?: boolean,
  documentSummaries?: Record<string, string>,
}

const DNADocumentListRow: React.FC<DNADocumentListRowProps> = React.memo((props) => {
  const {
    documentORM,
    isOnline,
    fields,
    variant,
    cellStyle,
    rowStyle,
    onPress,
    onThumbnailPress,
    actionCallbacks,
    renderBadgesInline,
    checked,
    onCheck,
    documentSummaries,
  } = props
  const documentActions = useDNADocumentActions();
  const { deviceMode } = useAppSettings();
  const isDesktop = deviceMode === 'desktop';
  // [TODO-2126] - Rather than checking network status and the isContentCached key here
  //               we should just expose a property on the Version ORM that combines both
  //               Something like "isUnavailable"
  const unavailableContent =
    !isOnline &&
    (!documentORM.relations.version.cachedDocumentVersionORM?.meta.assets.isContentCached ||
      documentORM.model.type === FileType.WEB)
  const { configsMap } = documentORM.meta.customValues;

  const MemoizedActionButtons = useMemo(() => (
    <DNABox fill alignX="end" alignY="center">
      <Iffy is={variant === Variant.MSL}>
        <DNABox>
          {onCheck &&
            <DNACheckbox
              testID="select-item-check-box"
              context="altBg"
              style={S.checkbox}
              checked={!!checked}
              onChange={() => onCheck?.(documentORM)}
            />
          }
          <DNAButton
            appearance="ghost"
            status={documentORM.meta?.bookmark?.isBookmarked ? 'primary' : 'subtle'}
            size={isDesktop ? 'md' : 'xl'}
            iconLeft={documentORM.meta?.bookmark?.isBookmarked ? 'star-fill' : 'star'}
            onPress={documentActions.bookmark(documentORM)}
          />

        </DNABox>
      </Iffy>
      <DNADocumentContextMenu
        documentORM={documentORM}
        variant={variant}
        actionCallbacks={actionCallbacks}
      >
        <DNAButton
          appearance="ghost"
          status="subtle"
          size={isDesktop ? 'md' : 'xl'}
          iconLeft="dots-three-vertical-bold"
        />
      </DNADocumentContextMenu>
    </DNABox>
  ), [documentORM, isDesktop, variant, unavailableContent, checked]);

  const safeOnPress = useCallback(() => {
    if (onPress && !unavailableContent) {
      onPress(documentORM)
    }
  }, [onPress, unavailableContent, documentORM])

  const safeOnThumbnailPress = useCallback(() => {
    if (onThumbnailPress) {
      onThumbnailPress(documentORM)
    }
  }, [onThumbnailPress, documentORM])

  const finalRowStyle = useMemo(() => util.mergeStyles(
    undefined,
    S.rowContainer,
    rowStyle,
    [S.unavailableContentRowContainer, unavailableContent],
    [{ backgroundColor: colors['color-ui2-brand-50'] }, !!checked],
  ), [unavailableContent, rowStyle, checked])

  // [TODO] - Break this into separate components
  const modelFieldRenderer = (field: ListFieldConfig) => {
    function getRegularFieldComponent(
      field: ListFieldConfig): ReactElement {
      return (
        <DNAText
          testID={`document-gridlist-${field.fieldLabel.toLowerCase()}`}
          style={[
            field.fieldLabel === 'Name' ? S.documentRowTitleText : S.documentRowFieldText,
            (field.fieldLabel === 'Name' && renderBadgesInline) && { flex: 1 },
          ]}
          numberOfLines={field.fieldLabel === 'Name' ? (renderBadgesInline ? 1 : 3) : VALUE_MAX_TEXT_LINES}
          ellipsizeMode={field.fieldLabel === 'Name' ? 'tail' : undefined}
        >
          {getFormattedModelValue(field, documentORM)}
        </DNAText>
      );
    }

    if (field.fieldName === 'status') {
      const documentVersion = documentORM.relations.version.latestDocumentVersionORM;
      const isDraft = documentORM.meta.hasUnpublishedVersion &&
        documentORM.model.status === DocumentStatus.PUBLISHED;
      const isScheduled = documentVersion.meta.schedule.publish.isScheduled;
      const scheduledPublish = documentVersion.meta.schedule.publish.scheduledAt;

      return documentORM.meta.hasUnpublishedVersion
        ? (
          <DNABox spacing="xs" key={documentORM.model.id}>
            <DNAPopover >
              <DNAPopover.Anchor>
                <DNADocumentStatusChip
                  documentStatus={documentORM.model.status}
                  isScheduled={isScheduled}
                  isDraft={isDraft}
                />
              </DNAPopover.Anchor>
              <DNAPopover.Content offset={-60}>
                {isScheduled ? (
                  <ScheduleDocumentTooltip
                    scheduledPublish={scheduledPublish}
                    colorMode="dark"
                  />
                ) : (
                  <DNAText numberOfLines={1} style={{ color: 'white' }}>New version in progress</DNAText>
                )}
              </DNAPopover.Content>
            </DNAPopover>
          </DNABox>
        )
        : (
          <DNADocumentStatusChip
            documentStatus={documentORM.model.status}
          />
        )
    } else if (field.fieldName === 'thumbnail') {
      const documentVersion = documentORM.relations.version.latestUsableDocumentVersionORM
      const isPublisherRoute = variant === Variant.publisher;

      const canPresent = documentORM.meta.permissions.MSLPresent
      const defaultOnPress = canPresent ? documentActions.present(documentORM) : documentActions.preview(documentORM)

      const handleThumbnailPress = () => {
        return isPublisherRoute ? safeOnThumbnailPress() : defaultOnPress()
      }

      return (
        <Pressable onPress={unavailableContent ? undefined : safeOnThumbnailPress}>
          <DNADocumentThumbnail.Hover
            width={GRID_LIST_STYLES_VARIANTS_CONFIGS.DOCUMENT_THUMBNAIL[deviceMode].width}
            height={GRID_LIST_STYLES_VARIANTS_CONFIGS.DOCUMENT_THUMBNAIL[deviceMode].height}
            documentVersionORM={documentVersion}
            showProcessing={variant === Variant.publisher}
            unavailableContent={unavailableContent}
            /** Custom behavior here. Thumbs in Publisher routes just open the preview modal,
             * whereas MSL views will either present or preview based on whether a doc is internal */
            onPress={unavailableContent ? undefined : handleThumbnailPress}
          />
        </Pressable>
      )
    } else if (field.fieldName === 'labelStatusDot') {
      return (
        <DNADocumentStatusDot documentORM={documentORM} />
      )
    } else if (field.fieldName === 'documentSummary') {
      const documentVersion = documentORM.relations.version.latestUsableDocumentVersionORM;
      if (!supportsDocumentSummary(documentVersion.model.type)) return null;
      const documentVersionId = documentVersion.model.id;

      return (
        <DNADocumentSummaryPreview
          key={documentVersionId}
          summary={documentSummaries?.[documentVersionId]}
          onPress={safeOnPress}
        />
      );
    } else if (field.fieldName === 'title') {
      const documentVersion = documentORM.relations.version.latestUsableDocumentVersionORM
      return (
        <>
          {renderBadgesInline ? (
            <DNABox appearance="row" fill alignY="center" spacing="xs" childStyle={[0, { maxWidth: '90%' }]}>
              {getRegularFieldComponent(field)}
              <DNACustomFieldChipList
                style={{ flexShrink: 0 }}
                documentVersionORM={documentVersion}
              />
            </DNABox>
          ) : (
            getRegularFieldComponent(field)
          )}
          <Iffy is={unavailableContent}>
            <DNAChip.Beacon
              testID="content-not-available"
              variant="CONTENT_UNAVAILABLE"
              style={S.unavailableContentBadge}
            />
          </Iffy>
        </>
      )
    } else {
      return getRegularFieldComponent(field);
    }
  }

  return (
    <GridList.Row
      testID="document-list-row"
      interactive="hoverable"
      hoverColor={luxColors.mainContainer.primary}
      onPress={safeOnPress}
      style={finalRowStyle}
    >
      {
        fields?.map(field => {
          const isActionMenuField = field.fieldType === 'ACTION_MENU';
          const isThumbnailField = field.fieldName === 'thumbnail' && field.fieldType === 'MODEL';
          const finalCellStyle = util.mergeStyles(
            undefined,
            cellStyle,
            [S.fieldCellContainer, (!isActionMenuField && !isThumbnailField)],
            [{ minWidth: 0 }, field.fieldName === 'title' && renderBadgesInline],
          )
          return (
            <GridList.Col key={field.fieldName} style={finalCellStyle}>
              <Iffy is={isActionMenuField}>
                {MemoizedActionButtons}
              </Iffy>
              <Iffy is={field.fieldType === 'MODEL' || field.fieldType === 'DOC_MODEL'}>
                {modelFieldRenderer(field)}
              </Iffy>
              <Iffy is={field.fieldType === 'LABEL'}>
                {field.isBadge
                  ? <DNACustomFieldChipList
                      documentVersionORM={documentORM.relations.version.latestUsableDocumentVersionORM}
                  />
                  : <DNAText
                      numberOfLines={VALUE_MAX_TEXT_LINES}
                      testID={`document-gridlist-${field.fieldLabel.toLowerCase()}`}
                      style={S.documentRowFieldText}
                  >
                    {configsMap?.[field.id ?? '-1']?.displayValues.join(', ')}
                  </DNAText>
                }
              </Iffy>
            </GridList.Col>
          )
        })
      }
    </GridList.Row>
  )
})

export default DNADocumentListRow;
